/**
 * Photo upload helper — orchestrates presign → S3 PUT → finalize.
 *
 * Two-step pattern (matches the backend FR-D2 contract):
 *   1. POST /v1/estimates/{id}/photos:presign  → photo_id + URL
 *   2. PUT  <upload_url> with the body         → 200 from S3
 *   3. POST /v1/estimates/{id}/photos:finalize → server confirms,
 *      generates thumbnail, kicks off extraction if anchors exist
 *
 * The `body` argument is generic so web (DOM `File`) and React Native
 * (a fetched Blob from a content URI) both consume the same code.
 */

import { finalizePhoto, presignPhoto } from "../api/sdk.gen";
import type { Photo, PhotoCategory } from "../api/types.gen";

export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const ALLOWED_MIMES = new Set(["image/jpeg", "image/png"]);

export type AllowedPhotoMime = "image/jpeg" | "image/png";

export interface UploadOutcome {
  ok: boolean;
  photo?: Photo;
  error?: string;
}

export interface UploadPhotoArgs {
  estimateId: string;
  mimeType: AllowedPhotoMime | string;
  bytes: number;
  /**
   * Returns the body to PUT to S3. Web passes `() => file`; native
   * passes `() => (await fetch(localUri)).blob()`. Lazy callback so
   * the body isn't materialised until we have a presigned URL — keeps
   * memory bounded. Typed loosely (`unknown`) because web's
   * `BodyInit` and React Native's `BodyInit_` aren't structurally
   * compatible — runtime accepts either.
   */
  getBody: () => Promise<unknown>;
  /**
   * What kind of image this is. Defaults to an ordinary photo; the
   * LiDAR room-scan snapshot passes `room_scan` so it's kept as a
   * project asset but excluded from Stella's photo/render context.
   */
  category?: PhotoCategory;
}

export async function uploadPhoto(args: UploadPhotoArgs): Promise<UploadOutcome> {
  const { estimateId, mimeType, bytes, getBody, category } = args;
  if (!ALLOWED_MIMES.has(mimeType)) {
    return { ok: false, error: "Photo must be a JPEG or PNG." };
  }
  if (bytes > PHOTO_MAX_BYTES) {
    return {
      ok: false,
      error: "Photo is too large; please pick a smaller one (≤ 10 MB).",
    };
  }

  const presign = await presignPhoto({
    estimateId,
    mimeType: mimeType as AllowedPhotoMime,
    bytes,
    category,
  });
  if ("error" in presign) {
    return { ok: false, error: presign.error.error.message };
  }
  const { photo_id, upload_url } = presign.data;

  let body: unknown;
  try {
    body = await getBody();
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Failed to read photo bytes",
    };
  }

  // S3 PUT must echo the same Content-Type we presigned for or the
  // signature is rejected.
  const putResp = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": mimeType },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body: body as any,
  });
  if (!putResp.ok) {
    return {
      ok: false,
      error: `Upload to storage failed (${putResp.status}). Please retry.`,
    };
  }

  const finalize = await finalizePhoto({ estimateId, photoId: photo_id });
  if ("error" in finalize) {
    return { ok: false, error: finalize.error.error.message };
  }
  return { ok: true, photo: finalize.data };
}

/**
 * Convenience wrapper for the web `File` API (legacy call site).
 * Equivalent to `uploadPhoto` with a body callback that returns the
 * file directly.
 */
export async function uploadPhotoFromFile(args: {
  estimateId: string;
  file: File;
}): Promise<UploadOutcome> {
  return uploadPhoto({
    estimateId: args.estimateId,
    mimeType: args.file.type,
    bytes: args.file.size,
    getBody: async () => args.file,
  });
}
